import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import userStore from '../../store/userStore';

/**
 * «Pon en marcha tu cobro»: los pasos que bloquean el arranque de una empresa
 * AR (docs/onboarding-ar-plan.md). Se marcan solos —lo calcula el servidor— y
 * cada uno lleva a la pantalla donde se hace.
 *
 * No molesta: se puede ocultar, y desaparece sola cuando está completa. Ocultarla
 * es cosa de este navegador (localStorage); no hace falta más.
 */
export const dismissKey = userId => `onboarding-oculta-${userId}`;

const readDismissed = userId => {
  try {
    return localStorage.getItem(dismissKey(userId)) === '1';
  } catch {
    return false;
  }
};

const OnboardingChecklist = () => {
  const { user } = userStore();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(() => readDismissed(user?.id));

  useEffect(() => {
    if (user?.role !== 'company' || dismissed) return undefined;
    let active = true;
    api
      .get('/onboarding/checklist')
      .then(({ data: res }) => {
        if (active) setData(res?.data || null);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [user?.role, dismissed]);

  if (user?.role !== 'company' || dismissed || !data?.applies || data.complete) return null;

  const ocultar = () => {
    try {
      localStorage.setItem(dismissKey(user?.id), '1');
    } catch {
      // Sin almacenamiento, se oculta hasta recargar.
    }
    setDismissed(true);
  };

  return (
    <section aria-label='Pon en marcha tu cobro' className='mb-8 rounded-lg border border-blue-200 bg-blue-50 p-5'>
      <div className='flex items-start justify-between gap-4'>
        <div>
          <h2 className='text-base font-semibold text-slate-800'>Pon en marcha tu cobro</h2>
          <p className='mt-1 text-sm text-slate-600'>
            Con estos pasos hechos, cada deudor que importes empieza a recibir sus avisos solo.{' '}
            <span className='font-medium text-slate-800'>
              {data.done} de {data.total}
            </span>
          </p>
        </div>
        <button
          type='button'
          onClick={ocultar}
          className='rounded-lg p-1 text-slate-500 hover:bg-blue-100 hover:text-slate-700'
          aria-label='Ocultar'
          title='Ocultar'
        >
          <XMarkIcon className='h-5 w-5' />
        </button>
      </div>

      <div className='mt-3 h-1.5 w-full rounded-lg bg-blue-100'>
        <div
          className='h-1.5 rounded-lg bg-blue-600'
          style={{ width: `${Math.round((data.done / data.total) * 100)}%` }}
        />
      </div>

      <ul className='mt-4 space-y-3'>
        {data.steps.map(step => (
          <li key={step.key} className='flex items-start gap-3'>
            {step.done ? (
              <CheckCircleIcon className='mt-0.5 h-5 w-5 flex-none text-green-600' aria-label='Hecho' />
            ) : (
              <span
                className='mt-0.5 h-5 w-5 flex-none rounded-full border-2 border-slate-300'
                aria-label='Pendiente'
              />
            )}
            <div>
              {step.done || !step.to ? (
                <p className={`text-sm font-medium ${step.done ? 'text-slate-500 line-through' : 'text-slate-800'}`}>
                  {step.label}
                </p>
              ) : (
                <Link to={step.to} className='text-sm font-medium text-blue-700 hover:underline'>
                  {step.label}
                </Link>
              )}
              {!step.done && <p className='text-xs text-slate-600'>{step.hint}</p>}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default OnboardingChecklist;
